export interface FindDegreesBenefit {
  icon:
    | 'GraduationCapIcon'
    | 'BookOpenIcon'
    | 'HeartIcon'
    | 'UsersIcon'
    | 'ShieldCheckIcon'
    | 'ClockIcon'
    | 'DollarSignIcon'
    | 'AwardIcon'
  title: string
  description: string
}

export interface FindDegreesTestimonial {
  quote: string
  name: string
  detail: string
}

export interface FindDegreesTemplate {
  slug: string
  metaTitle: string
  metaDescription: string
  // Hero
  eyebrow: string
  headline: string
  subheadline: string
  heroImage: string
  // New hero layout fields (optional for backward compat)
  heroEyebrowBadge?: string // small pill at top, e.g. "● NEXT SESSION STARTS SOON"
  heroHeadlinePrefix?: string // e.g. "Earn an"
  heroHeadlineHighlight?: string // e.g. "Online Degree" (rendered in accent red)
  heroHeadlineSuffix?: string // e.g. "On Your Schedule"
  heroCardHeading?: string // e.g. "Earn an Online Degree"
  heroCardSubtext?: string // e.g. "Next session starts soon."
  heroCardHelper?: string // e.g. helper line below CTA in the card
  heroStep1Label?: string // e.g. "Profile Complete"
  heroStep2Label?: string // e.g. "Confirm Email"
  // Primary CTA
  ctaLabel: string
  ctaHref: string
  // Quick trust strip
  trustItems: string[]
  // Value props / benefits
  benefitsHeading: string
  benefits: FindDegreesBenefit[]
  // Featured schools / logos (text only fallback)
  schoolsHeading: string
  schoolNames: string[]
  // Social proof
  testimonial?: FindDegreesTestimonial
  // Bottom CTA
  bottomCtaHeading: string
  bottomCtaBody: string
  bottomCtaLabel: string
  /**
   * Match-widget context this page represents, carried onto the funnel so the
   * visitor is not asked again for what the page already told us. Values are the
   * option `value` strings in `data/degrees.ts`.
   */
  matchParams?: {
    degreeInterest?: string
    areaStudy?: string
    mProgram?: string
  }
  // Theme color
  accentColor: string
  accentColorDark: string
}

export const findDegreesTemplates: FindDegreesTemplate[] = [
  {
    slug: 'online-christian-colleges',
    metaTitle:
      'Find an Online Christian College That Fits Your Calling | FindOnlineColleges',
    metaDescription:
      'Discover accredited online Christian colleges and faith-based universities. Compare programs in ministry, business, nursing, counseling, and more.',
    eyebrow: 'Faith-Based Online Education',
    headline: 'Earn Your Degree at an Online Christian College',
    subheadline:
      'Flexible, accredited online Christian programs designed for working adults. Explore your options and take the first step toward a faith-rooted future today.',
    heroImage:
      'https://images.unsplash.com/photo-1507692049790-de58290a4334?w=900&q=80&auto=format&fit=crop',
    heroEyebrowBadge: 'NEXT SESSION STARTS SOON',
    heroHeadlinePrefix: 'Earn an',
    heroHeadlineHighlight: 'Online Christian Degree',
    heroHeadlineSuffix: 'On Your Schedule',
    heroCardHeading: 'Earn an Online Christian Degree',
    heroCardSubtext: 'Next session starts soon.',
    heroCardHelper:
      'Want to learn more? Our admissions team is here to support you and get your questions answered.',
    heroStep1Label: 'Profile Complete',
    heroStep2Label: 'Confirm Email',
    ctaLabel: 'Get Started',
    ctaHref: '/guide/online-christian-colleges',
    trustItems: [
      'Flexible and Convenient',
      'Regionally Accredited Schools',
      'No Obligation',
      'Used by 2M+ Students',
    ],
    benefitsHeading: 'Why Choose an Online Christian College?',
    benefits: [
      {
        icon: 'BookOpenIcon',
        title: 'Faith-Integrated Learning',
        description:
          'Every course is taught from a biblical worldview — connect what you learn with what you believe.',
      },
      {
        icon: 'ClockIcon',
        title: 'Flexible for Working Adults',
        description:
          'Accelerated 8-week courses and asynchronous learning fit around work, family, and ministry.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited & Respected',
        description:
          'Earn a regionally accredited degree recognized by employers, grad schools, and licensing boards.',
      },
      {
        icon: 'DollarSignIcon',
        title: 'Affordable Tuition',
        description:
          'Discounted online rates, generous transfer credit, and scholarships for ministry and military.',
      },
      {
        icon: 'UsersIcon',
        title: 'Christian Community',
        description:
          'Connect with classmates and faculty who share your faith through virtual chapel and mentorship.',
      },
      {
        icon: 'AwardIcon',
        title: 'Programs for Every Calling',
        description:
          'From ministry and theology to nursing, counseling, business, and education — all faith-informed.',
      },
    ],
    schoolsHeading: 'Featured Christian Universities',
    schoolNames: [
      'Liberty University',
      'Grand Canyon University',
      'Regent University',
      'Colorado Christian University',
      'Indiana Wesleyan University',
      'Ohio Christian University',
    ],
    testimonial: {
      quote:
        "Going back to school felt impossible until I found an online Christian program that fit my life. I'm earning my degree while still leading worship at my church — and growing spiritually at the same time.",
      name: 'Sarah M.',
      detail: 'BA in Christian Ministry, Online Student',
    },
    bottomCtaHeading: 'Ready to Find Your Christian College?',
    bottomCtaBody:
      'Browse accredited online Christian colleges, compare programs, and take the next step toward a degree that aligns with your values.',
    bottomCtaLabel: 'Explore Christian Colleges',
    accentColor: '#4A3F8C', // navy (brand indigo-violet)
    accentColorDark: '#332B66',
  },
  {
    slug: 'request-information',
    metaTitle:
      'Request Information on Accredited Online Colleges | FindOnlineColleges',
    metaDescription:
      'Thinking about going back to school? Tell us how to reach you and we will send information on accredited online colleges built for working adults.',
    eyebrow: 'Going Back to School',
    headline: 'Thinking About Going Back to School Online?',
    subheadline:
      'You do not need to have your major picked out. Tell us how to reach you and we will send information on accredited online colleges built for adults who are working, raising a family, or returning after time away.',
    heroImage:
      'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900&q=80&auto=format&fit=crop',
    heroEyebrowBadge: 'NO OBLIGATION TO ENROLL',
    heroHeadlinePrefix: 'Thinking About',
    heroHeadlineHighlight: 'Going Back to School',
    heroHeadlineSuffix: 'Online?',
    heroCardHeading: 'Request Information',
    heroCardHelper:
      'We will use this to send information on accredited online colleges and, if you would like, connect you with an advisor who can answer your questions.',
    heroStep1Label: 'Your Details',
    heroStep2Label: 'Get Information',
    ctaLabel: 'Request Information',
    ctaHref: '/get-matched',
    trustItems: [
      'Accredited Schools Only',
      'No Obligation',
      'Used by 2M+ Students',
    ],
    benefitsHeading: 'What to Know Before You Go Back',
    benefits: [
      {
        icon: 'ClockIcon',
        title: 'You Can Start Sooner Than You Think',
        description:
          'Online colleges run start dates every few weeks rather than once a semester, so there is rarely a reason to wait until fall.',
      },
      {
        icon: 'AwardIcon',
        title: 'Old Credits Often Still Count',
        description:
          'Coursework from years ago, military training, and professional certifications frequently transfer — sometimes shortening a degree by a full year.',
      },
      {
        icon: 'DollarSignIcon',
        title: 'Aid Is Not Just for New Graduates',
        description:
          'Federal aid, grants, and employer tuition reimbursement are all open to returning adults. Most people qualify for more than they expect.',
      },
      {
        icon: 'UsersIcon',
        title: 'You Will Not Be the Oldest in Class',
        description:
          'The average online student is in their thirties and working full time. Programs are designed around that, not around campus life.',
      },
      {
        icon: 'BookOpenIcon',
        title: 'You Can Decide Your Major Later',
        description:
          'General education requirements are shared across most degrees, so you can begin before you have settled on a field.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accreditation Is the One Thing to Check',
        description:
          'It determines whether your credits transfer and whether employers recognize the degree. Every school we work with is accredited.',
      },
    ],
    schoolsHeading: 'Schools in Our Network',
    schoolNames: [
      'Grand Canyon University',
      'Liberty University',
      'Purdue Global',
      'Southern New Hampshire University',
      'University of Arizona Global Campus',
      'Ultimate Medical Academy',
    ],
    testimonial: {
      quote:
        'I left school in my twenties and assumed that was that. Fifteen years later I found out most of my credits still counted. I finished my bachelors online while working full time.',
      name: 'Renée A.',
      detail: 'BS in Business Administration, Online Student',
    },
    bottomCtaHeading: 'Ready to Find Out What Going Back Would Look Like?',
    bottomCtaBody:
      'Send us your details and we will follow up with information on accredited online colleges, start dates, and what it would take to finish. No obligation to enroll.',
    bottomCtaLabel: 'Request Information',
    accentColor: '#4A3F8C', // navy (brand indigo-violet)
    accentColorDark: '#332B66',
  },
  {
    slug: 'match-online-colleges',
    metaTitle:
      'Get Matched With Accredited Online Colleges | FindOnlineColleges',
    metaDescription:
      'Answer a few questions and get matched with accredited online colleges offering the degree you want. No cost, no obligation, and takes about two minutes.',
    eyebrow: 'No-Cost College Matching',
    headline: 'Get Matched With Online Colleges That Fit Your Goals',
    subheadline:
      'Tell us the degree you want, your background, and when you want to start. We will match you with accredited online colleges that fit — at no cost to you, and with no obligation to enroll.',
    heroImage:
      'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900&q=80&auto=format&fit=crop',
    heroEyebrowBadge: 'TAKES ABOUT 2 MINUTES',
    heroHeadlinePrefix: 'Find Your',
    heroHeadlineHighlight: 'Online College Match',
    heroHeadlineSuffix: 'In Just a Few Questions',
    heroCardHeading: 'Find Your Online College Match',
    heroCardSubtext: 'No cost to you — and no obligation to enroll.',
    heroCardHelper:
      'We ask a few questions about your degree goals, education history, and start date so the schools we show you can actually accept you.',
    heroStep1Label: 'Answer Questions',
    heroStep2Label: 'See Your Matches',
    ctaLabel: 'Find My Match',
    ctaHref: '/get-matched',
    trustItems: [
      'No Cost to You',
      'Accredited Schools Only',
      'No Obligation',
      'Used by 2M+ Students',
    ],
    benefitsHeading: 'Why Get Matched Instead of Searching Blind?',
    benefits: [
      {
        icon: 'GraduationCapIcon',
        title: 'Only Programs You Qualify For',
        description:
          'Matches account for your highest level of education and graduation year, so you are not reading about programs that will turn you away.',
      },
      {
        icon: 'ClockIcon',
        title: 'Two Minutes, Not Two Weekends',
        description:
          'Skip opening twenty tabs and filling out twenty separate forms. Answer once and see the schools that fit.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited Schools Only',
        description:
          'Every school we match you with is accredited by a recognized agency. No diploma mills, ever.',
      },
      {
        icon: 'DollarSignIcon',
        title: 'Compare Cost and Aid Upfront',
        description:
          'See tuition, transfer-credit policies, and financial aid options side by side before you talk to anyone.',
      },
      {
        icon: 'BookOpenIcon',
        title: 'Every Field and Degree Level',
        description:
          'Certificates through doctorates in business, nursing, IT, psychology, criminal justice, education, and more.',
      },
      {
        icon: 'UsersIcon',
        title: 'Built for Working Adults',
        description:
          'We prioritize online programs with flexible pacing and frequent start dates, so school fits around work and family.',
      },
    ],
    schoolsHeading: 'Schools in Our Network',
    schoolNames: [
      'Grand Canyon University',
      'Liberty University',
      'Purdue Global',
      'Southern New Hampshire University',
      'University of Arizona Global Campus',
      'Ultimate Medical Academy',
    ],
    testimonial: {
      quote:
        'I had no idea where to start. I answered a few questions, got three schools that actually took my old credits, and enrolled in the one that fit my schedule. It saved me weeks of research.',
      name: 'Marcus T.',
      detail: 'BS in Business Administration, Online Student',
    },
    bottomCtaHeading: 'Ready to See Your Online College Matches?',
    bottomCtaBody:
      'Answer a few quick questions about your degree goals and we will show you accredited online colleges that fit. It costs you nothing, and you are never obligated to enroll.',
    bottomCtaLabel: 'Get Matched Now',
    accentColor: '#4A3F8C', // navy (brand indigo-violet)
    accentColorDark: '#332B66',
  },
  {
    slug: 'online-psychology-degrees',
    metaTitle:
      'Online Psychology Degrees at Accredited Colleges | FindOnlineColleges',
    metaDescription:
      'Compare accredited online psychology degrees — associate through doctorate. Find programs in counseling, clinical, forensic, and organizational psychology.',
    eyebrow: 'Online Psychology Programs',
    headline: 'Earn an Online Psychology Degree Built Around Your Schedule',
    subheadline:
      'From an associate to a doctorate, accredited online psychology programs let you study human behavior without leaving your job. Tell us your goals and we will show you the programs that fit.',
    heroImage:
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=900&q=80&auto=format&fit=crop',
    heroEyebrowBadge: 'NEXT SESSION STARTS SOON',
    heroHeadlinePrefix: 'Earn an',
    heroHeadlineHighlight: 'Online Psychology Degree',
    heroHeadlineSuffix: 'On Your Schedule',
    heroCardHeading: 'Find Your Psychology Program',
    heroCardSubtext: 'No cost to you — and no obligation to enroll.',
    heroCardHelper:
      'Tell us your degree level and where you want to work, and we will match you with accredited psychology programs that fit.',
    heroStep1Label: 'Answer Questions',
    heroStep2Label: 'See Your Matches',
    ctaLabel: 'Find My Program',
    ctaHref: '/get-matched',
    trustItems: [
      'Accredited Schools Only',
      'Associate Through Doctorate',
      'No Obligation',
      'Used by 2M+ Students',
    ],
    benefitsHeading: 'Why Study Psychology Online?',
    benefits: [
      {
        icon: 'BookOpenIcon',
        title: 'Specialize in What Draws You',
        description:
          'Counseling, clinical, forensic, developmental, and industrial-organizational tracks are all available online.',
      },
      {
        icon: 'ClockIcon',
        title: 'Coursework That Fits Your Week',
        description:
          'Asynchronous lectures and 8-week terms mean you can study after shifts, after bedtime, or on weekends.',
      },
      {
        icon: 'UsersIcon',
        title: 'Skills Every Employer Wants',
        description:
          'Research methods, statistics, and human behavior transfer directly into HR, social services, and marketing roles.',
      },
      {
        icon: 'GraduationCapIcon',
        title: 'A Path Toward Licensure',
        description:
          'Bachelor’s programs feed into the master’s and doctoral degrees required for counseling and clinical licensure.',
      },
      {
        icon: 'DollarSignIcon',
        title: 'Transfer Credit Counts',
        description:
          'Many programs accept prior coursework and life experience, cutting both the time and the cost to finish.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited Programs Only',
        description:
          'We match you with regionally accredited schools whose credits transfer and whose degrees graduate boards recognize.',
      },
    ],
    schoolsHeading: 'Schools Offering Online Psychology Degrees',
    schoolNames: [
      'Grand Canyon University',
      'Liberty University',
      'Purdue Global',
      'Southern New Hampshire University',
      'University of Arizona Global Campus',
      'Capella University',
    ],
    testimonial: {
      quote:
        'I work full time at a group home, so campus was never going to happen. My online psychology courses were recorded, my advisor knew my schedule, and I finished my bachelor’s in three years without quitting my job.',
      name: 'Alicia R.',
      detail: 'BS in Psychology, Online Student',
    },
    bottomCtaHeading: 'Ready to Find Your Psychology Program?',
    bottomCtaBody:
      'Answer a few questions about your degree level and career goals, and we will show you accredited online psychology programs that fit. No cost, and no obligation to enroll.',
    bottomCtaLabel: 'See My Psychology Matches',
    // Bachelors + Psychology & Counseling + General Psychology & Counseling
    matchParams: {
      degreeInterest: '3',
      areaStudy: '3',
      mProgram: '300',
    },
    accentColor: '#4A3F8C', // navy (brand indigo-violet)
    accentColorDark: '#332B66',
  },
  {
    slug: 'online-criminal-justice-degrees',
    metaTitle:
      'Online Criminal Justice Degrees at Accredited Colleges | FindOnlineColleges',
    metaDescription:
      'Compare accredited online criminal justice degrees. Find programs in law enforcement, corrections, homeland security, and cybercrime built for working adults.',
    eyebrow: 'Online Criminal Justice Programs',
    headline: 'Earn an Online Criminal Justice Degree Around Your Shift',
    subheadline:
      'Accredited online criminal justice programs are built for people already working rotating shifts. Many accept academy and military training for credit — tell us your background and we will show you which ones fit.',
    heroImage:
      'https://images.unsplash.com/photo-1589391886645-d51941baf7fb?w=900&q=80&auto=format&fit=crop',
    heroEyebrowBadge: 'NEXT SESSION STARTS SOON',
    heroHeadlinePrefix: 'Earn an',
    heroHeadlineHighlight: 'Online Criminal Justice Degree',
    heroHeadlineSuffix: 'Around Your Shift',
    heroCardHeading: 'Find Your Criminal Justice Program',
    heroCardSubtext: 'No cost to you — and no obligation to enroll.',
    heroCardHelper:
      'Tell us your degree level and current role, and we will match you with accredited criminal justice programs that fit your schedule.',
    heroStep1Label: 'Answer Questions',
    heroStep2Label: 'See Your Matches',
    ctaLabel: 'Find My Program',
    ctaHref: '/get-matched',
    trustItems: [
      'Accredited Schools Only',
      'Academy & Military Credit',
      'No Obligation',
      'Used by 2M+ Students',
    ],
    benefitsHeading: 'Why Study Criminal Justice Online?',
    benefits: [
      {
        icon: 'ClockIcon',
        title: 'Built for Rotating Shifts',
        description:
          'Asynchronous coursework means no fixed class time to trade shifts around or miss when duty calls.',
      },
      {
        icon: 'AwardIcon',
        title: 'Credit for Academy Training',
        description:
          'Many schools award credit for police and corrections academy training, military service, and CLEP exams.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Specializations That Promote',
        description:
          'Homeland security, cybercrime, forensics, and corrections administration tracks map to real advancement paths.',
      },
      {
        icon: 'GraduationCapIcon',
        title: 'Meet Promotion Requirements',
        description:
          'Many agencies require a degree for sergeant, investigator, or federal roles — and some reimburse the tuition.',
      },
      {
        icon: 'UsersIcon',
        title: 'Faculty From the Field',
        description:
          'Courses are frequently taught by former officers, attorneys, and federal agents, not career academics.',
      },
      {
        icon: 'DollarSignIcon',
        title: 'Aid and Agency Tuition Help',
        description:
          'Federal aid, military benefits, and employer reimbursement can cover much of an online program’s cost.',
      },
    ],
    schoolsHeading: 'Schools Offering Online Criminal Justice Degrees',
    schoolNames: [
      'Purdue Global',
      'Liberty University',
      'Grand Canyon University',
      'Southern New Hampshire University',
      'University of Arizona Global Campus',
      'Colorado Technical University',
    ],
    testimonial: {
      quote:
        'I had eight years on patrol and no degree, which was blocking my move to investigations. The program took my academy credits, I studied between shifts, and I made detective before I finished.',
      name: 'Devon B.',
      detail: 'BS in Criminal Justice, Online Student',
    },
    bottomCtaHeading: 'Ready to Find Your Criminal Justice Program?',
    bottomCtaBody:
      'Answer a few questions about your background and career goals, and we will show you accredited online criminal justice programs that fit your schedule. No cost, and no obligation to enroll.',
    bottomCtaLabel: 'See My Criminal Justice Matches',
    // Bachelors + Criminal Justice, general program
    matchParams: {
      degreeInterest: '3',
      areaStudy: '2',
      mProgram: '200',
    },
    accentColor: '#4A3F8C', // navy (brand indigo-violet)
    accentColorDark: '#332B66',
  },
]

export function getFindDegreesTemplate(
  slug: string,
): FindDegreesTemplate | undefined {
  const normalized = slug.toLowerCase()
  return findDegreesTemplates.find((t) => t.slug.toLowerCase() === normalized)
}
